import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Native tabs keep their pages alive, so a routine changed from another tab (or browser tab)
// leaves this page showing the old routine. Whenever the page comes back into view, ask the
// server for the current routines version and refresh in place only if it moved on.
export default class extends Controller {
  static values = { url: String, version: String }

  initialize() {
    this.check = this.check.bind(this)
    this.checkIfVisible = this.checkIfVisible.bind(this)
    this.checkIfRestored = this.checkIfRestored.bind(this)
    this.adoptAfterSubmit = this.adoptAfterSubmit.bind(this)
    this.adopt = this.adopt.bind(this)
  }

  connect() {
    if (document.documentElement.hasAttribute("data-turbo-preview")) return

    document.addEventListener("visibilitychange", this.checkIfVisible)
    window.addEventListener("pageshow", this.checkIfRestored)
    // Both native apps send this when a page becomes active again, including on a tab switch.
    document.addEventListener("app-search:reconnect", this.check)
    // This page's own changes update it in place, so take on their version instead of refreshing.
    document.addEventListener("turbo:submit-end", this.adoptAfterSubmit)
    window.addEventListener("routine-guidance:logged", this.adopt)
    window.addEventListener("routine-handoff:applied", this.adopt)

    // A back navigation can restore this page from Turbo's cache.
    this.check()
  }

  disconnect() {
    this.abortController?.abort()
    document.removeEventListener("visibilitychange", this.checkIfVisible)
    window.removeEventListener("pageshow", this.checkIfRestored)
    document.removeEventListener("app-search:reconnect", this.check)
    document.removeEventListener("turbo:submit-end", this.adoptAfterSubmit)
    window.removeEventListener("routine-guidance:logged", this.adopt)
    window.removeEventListener("routine-handoff:applied", this.adopt)
  }

  checkIfVisible() {
    if (document.visibilityState === "visible") this.check()
  }

  checkIfRestored(event) {
    if (event.persisted) this.check()
  }

  adoptAfterSubmit(event) {
    if (event.detail.success) this.adopt()
  }

  async check() {
    const version = await this.fetchVersion()
    if (!version || version === this.versionValue) return

    // A refresh would close an open drawer; the next time the page comes into view checks again.
    if (document.hidden || document.querySelector("dialog[open]")) return

    this.versionValue = version
    this.dispatch("changed", { target: window })
    Turbo.session.refresh(document.baseURI)
  }

  async adopt() {
    const version = await this.fetchVersion()
    if (!version || version === this.versionValue) return

    this.versionValue = version
    this.dispatch("changed", { target: window })
  }

  async fetchVersion() {
    this.abortController?.abort()
    this.abortController = new AbortController()

    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal: this.abortController.signal
      })
      if (!response.ok) return null

      const { version } = await response.json()
      return version
    } catch {
      return null
    }
  }
}
